import React from 'react';

import { FastshiftRunbookStep } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import Button, { IconButton } from '@atlaskit/button/new';
import Form, { ErrorMessage, Field } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/core/cross';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';

import { messages } from '../messages';

type RunbookStepNoteFormData = { note?: string };

// Notes are captured as plain text and stored as an ADF document so the data
// shape stays compatible with a future rich editor (see the FastShift runbook
// rich-editor spike for why the AtlasKit editor is deferred for now).
const createPlainTextAdf = (text: string) => ({
	type: 'doc',
	version: 1,
	content: text
		.trim()
		.split(/\n+/)
		.filter(Boolean)
		.map((line) => ({
			type: 'paragraph',
			content: [{ type: 'text', text: line }],
		})),
});

export type RunbookStepNoteModalProps = {
	step: FastshiftRunbookStep | null;
	isLoading: boolean;
	onClose: () => void;
	/**
	 * Receives the newly added note as a stringified ADF document.
	 */
	onConfirm: (step: FastshiftRunbookStep, noteAdf?: string) => void;
};

export const RunbookStepNoteModal: React.FC<RunbookStepNoteModalProps> = ({
	step,
	isLoading,
	onClose,
	onConfirm,
}) => {
	const intl = useIntl();

	const handleSubmit = (data: RunbookStepNoteFormData) => {
		if (!step) {
			return undefined;
		}

		const text = (data.note ?? '').trim();
		if (!text) {
			return {
				note: intl.formatMessage(messages.runbooksPanelNoteModalEmptyError),
			};
		}

		onConfirm(step, JSON.stringify(createPlainTextAdf(text)));
		return undefined;
	};

	return (
		<ModalTransition>
			{step && (
				<Modal onClose={onClose} testId="runbook-step-note-modal">
					<Form<RunbookStepNoteFormData> key={step.stepId} onSubmit={handleSubmit}>
						{({ formProps }) => (
							<form {...formProps}>
								<ModalHeader>
									<ModalTitle>
										{intl.formatMessage(messages.runbooksPanelNoteModalTitle)}
									</ModalTitle>
									<IconButton
										appearance="subtle"
										spacing="compact"
										icon={CrossIcon}
										label={intl.formatMessage(messages.runbooksPanelNoteModalCloseButtonAriaLabel)}
										onClick={onClose}
										testId="runbook-step-note-modal-close"
									/>
								</ModalHeader>
								<ModalBody>
									<Stack space="space.100">
										<Text>{intl.formatMessage(messages.runbooksPanelNoteModalDescription)}</Text>
										<Field
											name="note"
											label={intl.formatMessage(messages.runbooksPanelNoteModalEditorLabel)}
											isRequired
										>
											{({ fieldProps, error }) => (
												<>
													{/* @ts-expect-error -- TextArea pointer-capture typings, matching the wac request-assistance modal usage */}
													<TextArea
														{...fieldProps}
														autoComplete="off"
														isRequired={false}
														resize="auto"
														minimumRows={7}
														placeholder={intl.formatMessage(
															messages.runbooksPanelNoteModalEditorPlaceholder,
														)}
														testId="runbook-step-note-input"
													/>
													{error && <ErrorMessage>{error}</ErrorMessage>}
												</>
											)}
										</Field>
									</Stack>
								</ModalBody>
								<ModalFooter>
									<ButtonGroup>
										<Button
											appearance="subtle"
											onClick={onClose}
											isDisabled={isLoading}
											type="button"
										>
											{intl.formatMessage(messages.runbooksPanelModalCancelButton)}
										</Button>
										<Button
											appearance="primary"
											isLoading={isLoading}
											testId="runbook-step-note-confirm-button"
											type="submit"
										>
											{intl.formatMessage(messages.runbooksPanelNoteModalConfirmButton)}
										</Button>
									</ButtonGroup>
								</ModalFooter>
							</form>
						)}
					</Form>
				</Modal>
			)}
		</ModalTransition>
	);
};
